import type {
  BulkUpsertAdvisorBody,
  BulkUpsertAdvisorPlansRes,
  BulkUpsertAdvisorResultRes
} from '@ijoin/hooks';

import { z } from 'zod';

export type AdvisorUploadColumn = {
  example: string;
  header: string;
  key: keyof BulkUpsertAdvisorBody;
  rule: string;
  type: 'boolean' | 'planIds' | 'text';
};

export const ADVISOR_UPLOAD_COLUMNS: AdvisorUploadColumn[] = [
  {
    example: 'jdoe@example.com',
    header: 'Username',
    key: 'username',
    rule: 'Required; matches an existing advisor to update',
    type: 'text'
  },
  {
    example: 'Jane',
    header: 'First Name',
    key: 'firstName',
    rule: 'Required, up to 32 characters',
    type: 'text'
  },
  {
    example: 'Doe',
    header: 'Last Name',
    key: 'lastName',
    rule: 'Required, up to 32 characters',
    type: 'text'
  },
  {
    example: 'jdoe@example.com',
    header: 'Email',
    key: 'email',
    rule: 'Optional, must be unique across advisors',
    type: 'text'
  },
  {
    example: '5555551234',
    header: 'Phone',
    key: 'phone',
    rule: 'Optional, 10 to 15 digits',
    type: 'text'
  },
  {
    example: '1234567',
    header: 'CRD Number',
    key: 'crdNumber',
    rule: 'Optional, no spaces',
    type: 'text'
  },
  {
    example: 'N',
    header: 'Has All Plans',
    key: 'hasAllPlans',
    rule: 'Y or N; when Y the Plan IDs column is ignored',
    type: 'boolean'
  },
  {
    example: 'Y',
    header: 'Can Create Email Campaigns',
    key: 'canCreateEmailCampaigns',
    rule: 'Y or N',
    type: 'boolean'
  },
  {
    example: 'N',
    header: 'Can Edit Email Campaigns',
    key: 'canEditEmailCampaigns',
    rule: 'Y or N',
    type: 'boolean'
  },
  {
    example: 'Y',
    header: 'Can Run Reports',
    key: 'canRunReports',
    rule: 'Y or N',
    type: 'boolean'
  },
  {
    example: 'N',
    header: 'Can View Sensitive Data',
    key: 'canViewSensitiveData',
    rule: 'Y or N; Y turns on two-factor for the login',
    type: 'boolean'
  },
  {
    example: 'N',
    header: 'Can IRA Prospect',
    key: 'canIraProspect',
    rule: 'Y or N',
    type: 'boolean'
  },
  {
    example: 'N',
    header: 'Can Investor View',
    key: 'canInvestorView',
    rule: 'Y or N',
    type: 'boolean'
  },
  {
    example: 'N',
    header: 'Is Admin',
    key: 'isAdmin',
    rule: 'Y or N',
    type: 'boolean'
  },
  {
    example: 'N',
    header: 'Is Locked',
    key: 'isLocked',
    rule: 'Y or N',
    type: 'boolean'
  },
  {
    example: '100234|100235',
    header: 'Plan IDs',
    key: 'externalPlanIds',
    rule: 'Record keeper plan ids separated by |',
    type: 'planIds'
  }
];

export const MAX_ADVISORS_PER_BATCH = 200;
export const MAX_PLAN_REFS_PER_BATCH = 20_000;

const TRUE_VALUES = ['1', 'true', 'y', 'yes'];
const FALSE_VALUES = ['0', 'false', 'n', 'no'];

const yesNo = z.preprocess(
  value =>
    typeof value === 'string' ? value.trim().toLowerCase() || undefined : value,
  z
    .enum([...TRUE_VALUES, ...FALSE_VALUES] as [string, ...string[]], {
      message: 'Use Y or N'
    })
    .transform(value => TRUE_VALUES.includes(value))
    .optional()
);

export const advisorUploadRowSchema = z.object({
  canCreateEmailCampaigns: yesNo,
  canEditEmailCampaigns: yesNo,
  canInvestorView: yesNo,
  canIraProspect: yesNo,
  canRunReports: yesNo,
  canViewSensitiveData: yesNo,
  crdNumber: z.preprocess(
    value => (typeof value === 'string' ? value.trim() || undefined : value),
    z.string().max(50).regex(/^\S+$/, 'No spaces allowed').optional()
  ),
  email: z.preprocess(
    value => (typeof value === 'string' ? value.trim() || undefined : value),
    z.string().email().max(128).optional()
  ),
  externalPlanIds: z.preprocess(
    value => {
      if (typeof value !== 'string') return value;

      const ids = [
        ...new Set(
          value
            .split(/[|;]/)
            .map(id => id.trim())
            .filter(Boolean)
        )
      ];

      return ids.length ? ids : undefined;
    },
    z.array(z.string().max(128)).optional()
  ),
  firstName: z.string().trim().min(1, 'Required').max(32),
  hasAllPlans: yesNo,
  isAdmin: yesNo,
  isLocked: yesNo,
  lastName: z.string().trim().min(1, 'Required').max(32),
  phone: z.preprocess(
    value =>
      typeof value === 'string' ? value.replace(/\D/g, '') || undefined : value,
    z.string().min(10, '10 to 15 digits').max(15, '10 to 15 digits').optional()
  ),
  username: z.string().trim().min(1, 'Required').max(128)
});

export type AdvisorUploadRow = z.infer<typeof advisorUploadRowSchema>;

export type UploadStatus =
  'invalid' | 'pending' | 'valid' | BulkUpsertAdvisorResultRes['status'];

export type UploadRow = Partial<AdvisorUploadRow> & {
  errors: string[];
  /** 1-based line in the file, header is line 1 */
  id: number;
  plans?: BulkUpsertAdvisorPlansRes;
  status: UploadStatus;
  username: string;
  warnings: string[];
};

/**
 * Validates each record and merges repeated usernames: later lines add plan ids
 * (how a client gets past a spreadsheet cell limit) and every other populated
 * field has to match the first occurrence.
 */
export const parseAdvisorUploadRows = (
  records: Record<string, unknown>[]
): UploadRow[] => {
  const rowsByKey = new Map<string, UploadRow>();

  records.forEach((record, index) => {
    const line = index + 2;
    const parsed = advisorUploadRowSchema.safeParse(record);

    if (!parsed.success) {
      rowsByKey.set(`line:${line}`, {
        errors: parsed.error.issues.map(
          issue =>
            `${ADVISOR_UPLOAD_COLUMNS.find(column => column.key === issue.path[0])?.header ?? 'Row'}: ${issue.message}`
        ),
        id: line,
        status: 'invalid',
        username: String(record.username ?? '').trim(),
        warnings: []
      });
      return;
    }

    const key = parsed.data.username.toLowerCase();
    const existing = rowsByKey.get(key);

    if (!existing) {
      rowsByKey.set(key, {
        ...parsed.data,
        errors: [],
        id: line,
        status: 'valid',
        warnings: []
      });
      return;
    }

    const conflicts = ADVISOR_UPLOAD_COLUMNS.filter(
      column =>
        column.type !== 'planIds' &&
        column.key !== 'username' &&
        parsed.data[column.key] !== undefined &&
        parsed.data[column.key] !== existing[column.key]
    );

    rowsByKey.set(key, {
      ...existing,
      errors: conflicts.length
        ? [
            ...existing.errors,
            `Line ${line} repeats this username with a different ${conflicts.map(column => column.header).join(', ')}`
          ]
        : existing.errors,
      externalPlanIds: [
        ...new Set([
          ...(existing.externalPlanIds ?? []),
          ...(parsed.data.externalPlanIds ?? [])
        ])
      ],
      status: conflicts.length ? 'invalid' : existing.status
    });
  });

  return [...rowsByKey.values()];
};

/** splits rows into request-sized batches: at most 200 advisors or 20,000 plan refs each */
export const toUploadBatches = (rows: UploadRow[]) =>
  rows.reduce<{ batches: UploadRow[][]; planRefs: number }>(
    (acc, row) => {
      const current = acc.batches.at(-1);
      const rowPlanRefs = row.externalPlanIds?.length ?? 0;
      const fits =
        current &&
        current.length < MAX_ADVISORS_PER_BATCH &&
        acc.planRefs + rowPlanRefs <= MAX_PLAN_REFS_PER_BATCH;

      return fits
        ? {
            batches: [...acc.batches.slice(0, -1), [...current, row]],
            planRefs: acc.planRefs + rowPlanRefs
          }
        : { batches: [...acc.batches, [row]], planRefs: rowPlanRefs };
    },
    { batches: [], planRefs: 0 }
  ).batches;
